import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchWorks } from "@/lib/works";
import { TYPE_LABELS } from "@/types/work";

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const HOT_KEYWORDS = ["长夜烬明", "星海拾光", "悬疑", "古装", "治愈", "科幻"];

export default function SearchDialog({ open, onClose }: SearchDialogProps) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setKeyword("");
      const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const suggestions = useMemo(() => {
    if (!keyword.trim()) return [];
    return searchWorks(keyword).slice(0, 5);
  }, [keyword]);

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) return;
    onClose();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-24 md:pt-32">
      <button
        type="button"
        aria-label="关闭搜索"
        className="absolute inset-0 h-full w-full bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-2xl animate-slide-down overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-search-line text-lg"></i>
          </span>
          <input
            ref={inputRef}
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit(keyword);
            }}
            placeholder="搜索影视剧名称、主演或剧情关键词"
            className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => submit(keyword)}
            className="shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            搜索
          </button>
        </div>

        <div className="max-h-[52vh] overflow-y-auto p-4">
          {suggestions.length > 0 ? (
            <ul className="space-y-1.5">
              {suggestions.map((work) => (
                <li key={work.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`/detail/${work.id}`);
                    }}
                    className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="h-14 w-10 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={work.cover}
                        alt={work.title}
                        title={`${work.title} 剧情介绍`}
                        className="h-full w-full object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground-950">
                        {work.title}
                      </span>
                      <span className="clamp-1 block text-xs text-foreground-500">
                        {work.shortIntro}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                      {TYPE_LABELS[work.type]}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div>
              <p className="mb-2.5 text-xs font-medium text-foreground-500">
                {keyword.trim() ? "没有找到匹配的影视内容" : "热门搜索"}
              </p>
              {!keyword.trim() && (
                <div className="flex flex-wrap gap-2">
                  {HOT_KEYWORDS.map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => setKeyword(word)}
                      className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}